import { humanizeEnum } from './Form.js';

export interface OpenChargeRow {
  chargeId: string;
  leaseId: string;
  propertyName: string;
  unitLabel: string;
  chargeType: string;
  dueDate: string | null;
  amount: string;
  balance: string;
  daysLate: number;
  /** This charge's lease's PENDING/PROCESSING payment total — not this
   *  specific charge's, since an in-flight payment isn't attributed to a
   *  particular charge until settlement. Same across every open charge on
   *  the same lease. */
  pendingPaymentAmount: string;
}

/**
 * Individual open charges, portfolio-wide — from `GET /api/ledger/open-charges`.
 *
 * Same shape decision as `BalanceTable`: this is a per-charge grain rather than
 * `BalanceTable`'s per-lease rollup, since a lease carrying a balance is
 * usually more than one open obligation (rent plus a late fee is the normal
 * case, not the exception). Money is rendered verbatim, never summed here.
 */
export function OpenChargesTable({
  charges,
  emptyMessage = 'No open charges.',
  caption,
}: {
  charges: OpenChargeRow[];
  emptyMessage?: string;
  caption?: string;
}) {
  if (charges.length === 0) {
    return <p className="muted">{emptyMessage}</p>;
  }

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>Property</th>
            <th>Unit</th>
            <th>Type</th>
            <th>Due</th>
            <th className="num">Amount</th>
            <th className="num">Balance</th>
            <th>Age</th>
          </tr>
        </thead>
        <tbody>
          {charges.map((charge) => {
            const pending = Number(charge.pendingPaymentAmount ?? '0');
            return (
              <tr key={charge.chargeId}>
                <td>{charge.propertyName}</td>
                <td>{charge.unitLabel}</td>
                <td>{humanizeEnum(charge.chargeType)}</td>
                <td>{charge.dueDate ?? '—'}</td>
                <td className="num">${charge.amount}</td>
                <td className="num">${charge.balance}</td>
                <td>
                  {charge.daysLate > 0 ? (
                    <span className="pill pill--late">{charge.daysLate}d late</span>
                  ) : (
                    <span className="pill">current</span>
                  )}
                  {pending > 0 ? (
                    <span className="pill pill--info" style={{ marginLeft: '0.35rem' }}>
                      ${charge.pendingPaymentAmount} processing
                    </span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {caption ? (
        <p className="muted" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
          {caption}
        </p>
      ) : null}
    </>
  );
}
